import React, { useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { makeStyles } from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';

import { useAdminGetUserById } from '../../hooks/admin-get-user-by-id.hook';
import { useAdminUpdateUser } from '../../hooks/admin-update-user.hook';
import { useDeactivateUser } from '../../hooks/deactivate-user.hook';
import { useRestoreUser } from '../../hooks/restore-user.hook';
import { useSendEcoWelcomeEmail } from '../../hooks/send-eco-welcome-email.hook';
import { AdminUpdateUserRequestDTO } from '../../types/dtos/admin-update-user.dto';
import { User } from '../../types/user.type';
import { UserRoles } from '../../types/user-role.type';
import { ADMIN_USERS_APP_PATHS } from '../../constants/users-paths.constants';
import {
  USER_DETAILS_CONFIRM_DIALOG_CONTENT,
  getUserDetailsTabs,
  resolveUserDetailsTab,
} from '../../constants/user-details.constants';
import UserInformationsTab from '../../components/Containers/UserInformationsTab.component';
import UserOrdersTab from '../../components/Containers/UserOrdersTab.component';
import UserDiscountsTab from '../../components/Containers/UserDiscountsTab.component';
import { UserContactsTab } from '../../components/Containers/UserContactsTab.component';
import { UserInvitationsTab } from '../../components/Containers/UserInvitationsTab.component';
import { UserDetailsHeader } from '../../components/Containers/UserDetailsHeader.component';
import { EcoWelcomeDialog } from '../../components/Modals/EcoWelcomeDialog.component';
import { ConfirmActionDialog } from '../../../shared/components/Modals/ConfirmActionDialog.component';
import { NotFoundState } from '../../../shared/components/States/NotFoundState.component';
import TabsController from '../../../shared/components/Tabs/ButtonTabsController';

type DialogState = { kind: 'none' } | { kind: 'confirm'; action: 'deactivate' | 'restore' } | { kind: 'eco-welcome' };

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: '86svh',
    gap: '1rem',
  },
});

export default function AdminUserDetailsView() {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const { userId } = useParams<{ userId: string }>();

  const { isLoading, user, refetch } = useAdminGetUserById({ userId });
  const { isSubmitting: isUpdating, updateUser } = useAdminUpdateUser();
  const { deactivateUser, isSubmitting: isDeactivating } = useDeactivateUser();
  const { restoreUser, isSubmitting: isRestoring } = useRestoreUser();
  const { sendEcoWelcomeEmail, isSubmitting: isSendingWelcome } = useSendEcoWelcomeEmail();

  const [dialog, setDialog] = useState<DialogState>({ kind: 'none' });

  const userName = user ? `${user.firstname} ${user.lastname}` : '';
  const isEco = user?.role === UserRoles.ECO;
  const currentTab = resolveUserDetailsTab(new URLSearchParams(location.search).get('tab'), isEco);

  const closeDialog = () => setDialog({ kind: 'none' });

  const handleTabChange = (tab: string) => {
    const newParams = new URLSearchParams(location.search);
    newParams.set('tab', tab);
    history.push({ search: newParams.toString() });
  };

  const handleUserUpdated = async (editedFields: AdminUpdateUserRequestDTO): Promise<User | null> => {
    if (isLoading || !user) return null;

    const previousRole = user.role;
    const updatedUser = await updateUser(user.id, editedFields);
    if (!updatedUser) return null;

    const isEcoPromotion = previousRole !== UserRoles.ECO && updatedUser.role === UserRoles.ECO;
    if (isEcoPromotion && updatedUser.isActive) setDialog({ kind: 'eco-welcome' });
    await refetch();
    return updatedUser;
  };

  const handleConfirm = async () => {
    if (dialog.kind !== 'confirm' || !user) return;

    const result = dialog.action === 'deactivate' ? await deactivateUser(user.id) : await restoreUser(user.id);
    if (!result) return;

    closeDialog();
    await refetch();
  };

  const handleSendEcoWelcome = async () => {
    if (!user) return;

    const isSent = await sendEcoWelcomeEmail(user.id);
    if (isSent) closeDialog();
  };

  return (
    <section className={classes.root}>
      {isLoading && <Skeleton variant="rect" animation="wave" height={92} />}
      {!isLoading && !user && (
        <NotFoundState
          title="Client introuvable"
          backLabel="Retour aux clients"
          backTo={ADMIN_USERS_APP_PATHS.USERS_LIST}
        />
      )}
      {!isLoading && user && (
        <React.Fragment>
          <UserDetailsHeader
            user={user}
            onStatusActionClick={() => setDialog({ kind: 'confirm', action: user.isActive ? 'deactivate' : 'restore' })}
          />
          <TabsController
            size="small"
            tabs={getUserDetailsTabs(isEco)}
            currentTabId={currentTab}
            onTabChange={handleTabChange}
          />
          {currentTab === 'informations' && (
            <UserInformationsTab user={user} isUpdating={isUpdating} updateUser={handleUserUpdated} />
          )}
          {currentTab === 'orders' && <UserOrdersTab user={user} />}
          {isEco && currentTab === 'invitations' && <UserInvitationsTab userId={user.id} />}
          {isEco && currentTab === 'contacts' && <UserContactsTab userId={user.id} />}
          {currentTab === 'discounts' && <UserDiscountsTab user={user} />}
        </React.Fragment>
      )}
      {dialog.kind === 'confirm' && (
        <ConfirmActionDialog
          open
          title={USER_DETAILS_CONFIRM_DIALOG_CONTENT[dialog.action].title}
          description={USER_DETAILS_CONFIRM_DIALOG_CONTENT[dialog.action].describe(userName)}
          confirmLabel={USER_DETAILS_CONFIRM_DIALOG_CONTENT[dialog.action].confirmLabel}
          isSubmitting={isDeactivating || isRestoring}
          onConfirm={handleConfirm}
          onClose={closeDialog}
        />
      )}
      {dialog.kind === 'eco-welcome' && (
        <EcoWelcomeDialog
          open
          userName={userName}
          isSubmitting={isSendingWelcome}
          onSend={handleSendEcoWelcome}
          onLater={closeDialog}
        />
      )}
    </section>
  );
}
